// The two ways a picker opens, the bar under it, and the field that opens it.
//
// On a desktop the legacy pickers floated a panel under the field (MUI's
// Popper); on a phone they opened a Dialog with rounded corners and the
// CustomActionBar. The calendar and the time wheel both open through these, so
// that they open the same way.

import type { ReactNode } from "react";
import {
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  Fade,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  useTheme,
} from "@mui/material";
import { useT } from "~/i18n";

/**
 * Floating is the desktop panel. A press anywhere else is reported; what it
 * means — commit for the time wheel, dismiss for the calendar — is the caller's.
 */
export function Floating({
  open,
  anchor,
  onClickAway,
  elevation = 8,
  children,
}: {
  open: boolean;
  anchor: HTMLElement | null;
  onClickAway: () => void;
  elevation?: number;
  children: ReactNode;
}) {
  const theme = useTheme();
  return (
    <Popper
      open={open && !!anchor}
      anchorEl={anchor}
      placement="bottom-start"
      transition
      sx={{ zIndex: theme.zIndex.tooltip + 1 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps}>
          <div>
            <ClickAwayListener
              onClickAway={(e) => {
                if (anchor && anchor.contains(e.target as Node)) return;
                onClickAway();
              }}
            >
              <Paper elevation={elevation} sx={{ backgroundImage: "none" }}>
                {children}
              </Paper>
            </ClickAwayListener>
          </div>
        </Fade>
      )}
    </Popper>
  );
}

/** Modal is the phone's dialog, the legacy mobilePaper: pt 2, radius 24. */
export function Modal({
  open,
  onDismiss,
  maxWidth = false,
  children,
}: {
  open: boolean;
  onDismiss: () => void;
  maxWidth?: "xs" | false;
  children: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onClose={onDismiss}
      fullWidth={maxWidth !== false}
      maxWidth={maxWidth}
      sx={{
        "& .MuiDialog-paper": {
          pt: maxWidth === false ? 2 : 0,
          borderRadius: 6,
          backgroundImage: "none",
          minWidth: 320,
        },
      }}
    >
      {children}
    </Dialog>
  );
}

/** ActionBar is the legacy CustomActionBar: two contained pills. */
export function ActionBar({ onCancel, onAccept }: { onCancel: () => void; onAccept: () => void }) {
  const t = useT();
  const theme = useTheme();
  const sx = {
    backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main",
    textTransform: "none",
    border: theme.palette.mode === "dark" ? 1 : 0,
    borderColor: "primary.main",
    borderRadius: 6,
    "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main" },
  } as const;
  return (
    <DialogActions>
      <Button variant="contained" onClick={onCancel} sx={sx}>
        {t("cancel")}
      </Button>
      <Button variant="contained" onClick={onAccept} sx={sx}>
        {t("accept")}
      </Button>
    </DialogActions>
  );
}

/**
 * PickerField is the read-only outlined TextField every legacy picker drew:
 * the label always shrunk, a click opening the picker, and — where the legacy
 * one had it — an icon button at the end.
 */
export function PickerField({
  label,
  value,
  onOpen,
  focused = false,
  disabled = false,
  center = false,
  icon,
  fullWidth = true,
  sx,
}: {
  label?: string;
  value: string;
  onOpen?: (anchor: HTMLElement) => void;
  focused?: boolean;
  disabled?: boolean;
  center?: boolean;
  icon?: ReactNode;
  fullWidth?: boolean;
  sx?: object;
}) {
  return (
    <TextField
      variant="outlined"
      label={label}
      value={value}
      fullWidth={fullWidth}
      onClick={(e) => {
        if (!disabled) onOpen?.(e.currentTarget as HTMLElement);
      }}
      inputProps={{ readOnly: true }}
      focused={focused || undefined}
      disabled={disabled}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        endAdornment: icon ? (
          <InputAdornment position="end">
            <IconButton edge="end" disabled={disabled}>
              {icon}
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
      sx={{ ...(center ? { input: { textAlign: "center" } } : {}), ...sx }}
    />
  );
}
